import { RemoveRedEyeOutlined as RemoveRedEyeOutlinedIcon } from "@mui/icons-material";
import {
	Box,
	Chip,
	IconButton,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material";
import { format, parseISO } from "date-fns";
import { ISinisterList } from "../../../shared/services/api/sinister/SinisterService";
import { globalMaskDocument, globalTranslate } from "../../../shared/utils";
import { getStatusColorsSinister } from "../../../shared/utils/globalColorSinister";
import { HtmlTooltip } from "../../../infra/components/tooltipsCustom/tooltipsCuston"; // import do seu tooltip customizado
import { globalTranslateSinister } from "../../../shared/utils/globalTranslateSinister";

interface Props {
	rows: ISinisterList[];
	totalCount: number;
	page: number;
	rowsPerPage: number;
	onPageChange: (newPage: number) => void;
	onRowsPerPageChange: (value: number) => void;
}

export const TableSinister = ({ rows, totalCount, page, rowsPerPage, onPageChange, onRowsPerPageChange }: Props) => {
	const URL = window.location.protocol + "//" + window.location.host;

	return (
		<TableContainer
			component={Paper}
			variant="elevation"
			sx={{ mt: 1, width: "96%", px: { xs: 1, sm: 3 }, py: { xs: 2, sm: 3 }, ml: 1, borderRadius: 2, overflow: "hidden" }}
		>
			<Table size="small" stickyHeader>
				<TableHead>
					<TableRow sx={{ backgroundColor: "grey.100" }}>
						{["Nome", "Parceiro", "Endereço", "Responsável", "Status", "Cadastro", "Ações"].map((header, i) => (
							<TableCell key={i} align={i === 5 ? "center" : i > 2 ? "center" : "left"}>
								<Typography variant="button">{header}</Typography>
							</TableCell>
						))}
					</TableRow>
				</TableHead>

				<TableBody>
					{rows.map((row) => {
						const { bg, color } = getStatusColorsSinister(row.status);
						const createdAtDate = parseISO(row.createdAt ?? "");

						return (
							<TableRow key={row.id} hover>
								{/* Nome com HtmlTooltip */}
								<TableCell>
									<HtmlTooltip
										title={
											<Box>
												<Typography variant="subtitle2" fontWeight="bold">
													{row.proposal.name}
												</Typography>
											</Box>
										}
										arrow
										placement="top"
									>
										<Box>
											<Typography
												variant="body2"
												sx={{ maxWidth: "26ch", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
											>
												{row.proposal.name || "Sem nome"}
											</Typography>
											<Typography variant="caption" color="text.secondary">
												{globalMaskDocument(row.proposal.document)}
											</Typography>
										</Box>
									</HtmlTooltip>
								</TableCell>

								{/* Parceiro (sem tooltip, mas mantendo layout) */}
								<TableCell>
									<HtmlTooltip
										title={
											<Box>
												<Typography variant="subtitle2" fontWeight="bold">
													{row.client?.name}
												</Typography>
											</Box>
										}
										arrow
										placement="top"
									>
										<Box>
											<Typography
												variant="body2"
												sx={{ maxWidth: "26ch", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
											>
												{row.client?.name}
											</Typography>
											<Typography variant="caption" color="text.secondary">
												{globalMaskDocument(row.client?.document)}
											</Typography>
										</Box>
									</HtmlTooltip>
								</TableCell>

								{/* Endereço com HtmlTooltip */}
								<TableCell>
									{row.proposal.addressStreet ? (
										<HtmlTooltip
											title={
												<Box>
													<Typography variant="subtitle2" fontWeight="bold">
														Endereço completo
													</Typography>
													<Typography variant="body2">
														{`${row.proposal.addressStreet}, ${row.proposal.addressNumber} ${
															row.proposal.addressComplement ? `- ${row.proposal.addressComplement}` : ""
														} - ${row.proposal.addressDistrict}`}
													</Typography>
													<Typography variant="body2">{`${row.proposal.addressCity} - ${row.proposal.addressState}`}</Typography>
												</Box>
											}
											arrow
											placement="top"
										>
											<Box>
												<Typography variant="body2" noWrap sx={{ maxWidth: 230, overflow: "hidden", textOverflow: "ellipsis" }}>
													{`${row.proposal.addressStreet}, ${row.proposal.addressNumber}${
														row.proposal.addressComplement ? ` - ${row.proposal.addressComplement}` : ""
													} - ${row.proposal.addressDistrict}`}
												</Typography>
												<Typography variant="caption" color="text.secondary">
													{`${row.proposal.addressCity} - ${row.proposal.addressState}`}
												</Typography>
											</Box>
										</HtmlTooltip>
									) : (
										<Typography variant="body2" color="textSecondary">
											Endereço não cadastrado
										</Typography>
									)}
								</TableCell>
								<TableCell align="center">
									<HtmlTooltip
										title={
											<Box>
												<Typography variant="subtitle2" fontWeight="bold">
													Responsável pelo sinistro
												</Typography>
												<Typography variant="body2">{row.sinisterResponsible?.name || ""}</Typography>
												<Typography variant="body2">{row.responsibleEmail || row.sinisterResponsible?.email || ""}</Typography>
											</Box>
										}
										arrow
										placement="top"
									>
										<Stack spacing={0.2}>
											<Typography variant="body2" color="textSecondary">
												{row.sinisterResponsible?.name || ""}
											</Typography>
										</Stack>
									</HtmlTooltip>
								</TableCell>

								{/* (demais colunas mantidas igual) */}
								<TableCell align="center">
									<Box display="flex" flexDirection="column" alignItems="center">
										<Chip
											size="medium"
											label={globalTranslateSinister(row.status, "sinister")}
											sx={{
												color: color,
												backgroundColor: bg,
												fontWeight: 500,
												border: "0.5px solid #f1f1f1",
											}}
										/>
										<Typography variant="caption" color="textSecondary">
											{row.busyProperty === "busy" ? "Imóvel ocupado" : "Imóvel desocupado"}
										</Typography>
									</Box>
								</TableCell>

								<TableCell align="center">
									<Stack spacing={0.2}>
										<Typography variant="body2" color="textSecondary">
											{format(createdAtDate, "dd/MM/yyyy")}
										</Typography>
										<Typography variant="body2" color="textSecondary">
											{format(createdAtDate, "HH:mm:ss")}
										</Typography>
									</Stack>
								</TableCell>

								<TableCell align="center">
									<Tooltip title="Visualizar Sinistro">
										<IconButton onClick={() => window.open(`${URL}/sinistros/detalhe/${row.id}`)}>
											<RemoveRedEyeOutlinedIcon sx={{ color: "#0170B3" }} />
										</IconButton>
									</Tooltip>
								</TableCell>
							</TableRow>
						);
					})}
				</TableBody>
			</Table>

			<Box display="flex" justifyContent="flex-end" alignItems="center" mt={2} pr={2}>
				<TablePagination
					component="div"
					count={totalCount}
					page={page}
					rowsPerPage={rowsPerPage}
					onPageChange={(_, newPage) => onPageChange(newPage)}
					onRowsPerPageChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
					rowsPerPageOptions={[10, 25, 50]}
					labelRowsPerPage="Linhas por página:"
					labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count} registros`}
				/>
			</Box>
		</TableContainer>
	);
};
